<template>
  <div id="orders-rewards-by-month">
    <v-card class="px-4 pt-2 pb-4 my-0 elevation-0">
      <v-card-title class="px-1">
        <h1 class="card-title mb-1">
          {{ $t(`views.dashboardDetail.dividends.ordersRewardsByMonth.title`) }}
          <v-chip-group v-model="dateType" active-class="primary" mandatory column class="ml-3">
            <v-chip filter value="month" small class="mx-0">
              {{ $t('common.month') }}
            </v-chip>
            <span class="mx-1">/</span>
            <v-chip filter value="year" small class="mx-0">
              {{ $t('common.year') }}
            </v-chip>
          </v-chip-group>
        </h1>
      </v-card-title>
      <v-card-text class="mt-0 pa-0">
        <BarLoading v-if="!showTable && !loadedData" />
        <BarChart
          v-if="loadedData"
          ref="barChart"
          :chartData="barChartData"
          :chartOptions="barChartOptions"
        />

        <template v-if="showTable">
          <div class="my-5" />
          <v-data-table
            id="orders-rewards-by-month-table"
            :headers="headers"
            :items="mySummary"
            class="elevation-0 left-sticky-table"
            mobile-breakpoint="0"
            :loading="!loadedData"
            :items-per-page="5"
            :page.sync="currentPage"
            :item-class="itemRowBackground"
            :dense="user.preferences.denseTables"
            show-expand
            single-expand
          >
            <!-- <template slot="body.prepend">
              <tr v-if="totalRow" class="row-total">
                <td>TOTAL</td>
                <template v-if="type === 'scrip'">
                  <td class="text-right">
                    <currency :value="totalRow.positionNumber" />
                  </td>
                </template>
                <template v-else>
                  <td class="text-right">
                    <currency :value="totalRow.totalBaseCurrency" :currency="user.baseCurrency" />
                  </td>
                  <td class="text-right">
                    <currency
                      :value="totalRow.totalWithComissionBaseCurrency"
                      :currency="user.baseCurrency"
                      strong
                    />
                  </td>
                </template>
              </tr>
            </template> -->
            <template v-slot:item._id="{ item }">
              <div class="items-flex">
                <span>
                  <template v-if="dateType === 'month'">
                    <strong>{{ $t(`common.months.${item._id.month}`) }} {{ item._id.year }}</strong>
                  </template>
                  <template v-else>
                    <strong>{{ item._id }}</strong>
                  </template>
                </span>
                <template v-if="someNullExchangeRate(item)">
                  <div class="ml-3">
                    <WarningIcon :text="$t(`views.dashboardDetail.orders.ordersByMonth.warning`)" />
                  </div>
                </template>
              </div>
            </template>
            <template v-slot:item.totalBaseCurrency="{ item }">
              <currency :value="item.totalBaseCurrency" :currency="user.baseCurrency" />
            </template>
            <template v-slot:item.totalWithComissionBaseCurrency="{ item }">
              <currency
                :value="item.totalWithComissionBaseCurrency"
                :currency="user.baseCurrency"
                strong
              />
            </template>

            <template v-slot:item.data-table-expand="{ item, expand, isExpanded }">
              <v-icon v-if="item.types" size="25" class="ml-4" @click="expand(!isExpanded)">
                {{ !isExpanded ? 'mdi-chevron-down' : 'mdi-chevron-up' }}
              </v-icon>
            </template>

            <template v-slot:expanded-item="{ item }">
              <template v-if="item.types">
                <td :colspan="headers.length" class="pt-2 pb-3 px-1">
                  <v-simple-table class="type-detail-table" dense>
                    <template v-slot:default>
                      <thead>
                        <tr>
                          <th></th>
                          <th class="text-center">
                            {{ $t('views.orders.ordersTable.total') }}
                            ({{ user.baseCurrency }})
                          </th>
                          <th class="text-center">
                            {{ $t('views.orders.ordersTable.totalWithComission') }}
                            ({{ user.baseCurrency }})
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(type, idx) in item.types" :key="idx">
                          <td>
                            {{
                              $t(
                                `views.dashboardDetail.dividends.ordersRewardsByMonth.${type.type}`
                              )
                            }}
                          </td>
                          <td class="text-center">
                            <currency
                              v-if="type.totalBaseCurrency"
                              :value="type.totalBaseCurrency"
                              :currency="user.baseCurrency"
                            />
                            <span v-else></span>
                          </td>
                          <td class="text-center">
                            <currency
                              v-if="type.totalWithComissionBaseCurrency"
                              :value="type.totalWithComissionBaseCurrency"
                              :currency="user.baseCurrency"
                            />
                            <span v-else></span>
                          </td>
                        </tr>
                      </tbody>
                    </template>
                  </v-simple-table>
                </td>
              </template>
            </template>
          </v-data-table>
        </template>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import { map } from 'lodash'
import { mapState } from 'vuex'
import { getOrderSummary } from '@/lib/api.js'
import { DARK_DEFAULT_PALETTE, DEFAULT_PALETTE, ORDERS_TYPE_PALETTE } from '@/lib/colors.js'
import { getCurrencyValueAsString, getCurrencySymbol } from '@/lib/utils/currencies.js'
import Currency from '@/views/components/common/Currency'
import BarChart from '@/views/components/core/chartjs/BarChart'
import BarLoading from '@/views/components/core/BarLoading'
import TableTicker from '@/views/components/common/TableTicker'
import WarningIcon from '@/views/components/common/WarningIcon.vue'

export default {
  components: { Currency, BarChart, BarLoading, TableTicker, WarningIcon },
  props: {
    showTable: {
      type: Boolean,
      default: true,
    },
  },
  data: () => ({
    summary: null,
    mySummary: [],
    // totalRow: null,
    currentPage: null,
    loadedData: false,
    dateType: 'month',
    barChartData: {
      labels: null,
      datasets: null,
    },
  }),
  computed: {
    ...mapState(['user', 'hideAmounts']),
    barChartOptions() {
      const context = this
      return {
        maintainAspectRatio: false,
        scales: {
          xAxes: [
            {
              gridLines: {
                display: false,
              },
              stacked: true,
            },
          ],
          yAxes: [
            {
              stacked: true,
              ticks: {
                callback: function (value) {
                  return getCurrencyValueAsString({
                    value,
                    currency: context.user.baseCurrency,
                    hide: context.hideAmounts,
                  })
                },
              },
            },
          ],
        },
        tooltips: {
          callbacks: {
            label: function (tooltipItem, data) {
              let label = data.datasets[tooltipItem.datasetIndex].label || ''
              if (label) label += ': '
              label += getCurrencyValueAsString({
                value: tooltipItem.yLabel,
                currency: context.user.baseCurrency,
                hide: context.hideAmounts,
              })
              return label
            },
          },
        },
      }
    },
    headers() {
      return [
        {
          text:
            this.dateType === 'month'
              ? this.$t('views.orders.ordersTable.month')
              : this.$t('views.orders.ordersTable.year'),
          value: '_id',
          sortable: false,
          divider: true,
        },
        {
          text: `${this.$t('views.orders.ordersTable.total')} (${this.user.baseCurrency})`,
          value: 'totalBaseCurrency',
          align: 'right',
        },
        {
          text: `${this.$t('views.orders.ordersTable.totalWithComission')} (${getCurrencySymbol(
            this.user.baseCurrency
          )})`,
          value: 'totalWithComissionBaseCurrency',
          align: 'right',
        },

        {
          text: '',
          value: 'data-table-expand',
          sortable: false,
          align: 'right',
        },
      ]
    },
  },
  watch: {
    dateType: {
      handler() {
        this.changeType()
      },
    },
  },
  methods: {
    someNullExchangeRate(item) {
      return item.exchangeRates && item.exchangeRates.some((elem) => elem.some((e) => e === null))
    },
    itemRowBackground(item) {
      if (this.someNullExchangeRate(item)) return 'row-advise'
    },
    getData(data, orderType) {
      return data.map((elem) => {
        if (!elem.types || !elem.types.length) return 0
        const bonus = elem.types.find((type) => type.type === orderType)
        if (!bonus) return 0
        return bonus.totalWithComissionBaseCurrency
      })
    },
    createBarChartObject() {
      const summary = this.mySummary.slice().reverse()

      this.barChartData.datasets = [
        {
          label: this.$t('views.dashboardDetail.dividends.ordersRewardsByMonth.stakeReward'),
          // this.$t('views.dashboardDetail.dividends.ordersRewardsByMonth.stakeReward') +
          // `${this.$t('views.orders.ordersTable.totalWithComission')} (${this.user.baseCurrency})`,
          data: this.getData(summary, 'stakeReward'),
          backgroundColor: this.$vuetify.theme.dark
            ? DARK_DEFAULT_PALETTE.chartsBarGreen
            : DEFAULT_PALETTE.chartsBarGreen,
        },
        {
          label: this.$t('views.dashboardDetail.dividends.ordersRewardsByMonth.bonus'),
          // this.$t('views.dashboardDetail.dividends.ordersRewardsByMonth.bonus') +
          // `${this.$t('views.orders.ordersTable.totalWithComission')} (${this.user.baseCurrency})`,
          data: this.getData(summary, 'bonus'),
          backgroundColor: this.$vuetify.theme.dark
            ? DARK_DEFAULT_PALETTE.chartsBarBlue
            : DEFAULT_PALETTE.chartsBarBlue,
        },
        {
          label: this.$t('views.dashboardDetail.dividends.ordersRewardsByMonth.drip'),
          // this.$t('views.dashboardDetail.dividends.ordersRewardsByMonth.drip') +
          // `${this.$t('views.orders.ordersTable.totalWithComission')} (${this.user.baseCurrency})`,
          data: this.getData(summary, 'drip'),
          backgroundColor: this.$vuetify.theme.dark
            ? DARK_DEFAULT_PALETTE.dark_backgroundSoftBlue
            : ORDERS_TYPE_PALETTE.drip,
        },
      ]

      let labels
      if (this.dateType === 'month') {
        labels = map(
          summary,
          (item) =>
            this.$t(`common.months.${item._id.month}`).substring(0, 3) +
            ` ${item._id.year.toString().slice(-2)}`
        )
      } else {
        labels = map(summary, (item) => item._id)
      }
      this.barChartData.labels = labels

      if (this.$refs.barChart) this.$refs.barChart.render()
    },
    changeType() {
      const summary = JSON.parse(JSON.stringify(this.summary))

      if (summary.length && this.dateType === 'month') {
        // let summaryMonths = [summary[0]]
        let summaryMonths = []
        summary.forEach((year) => {
          if (year.months) summaryMonths = summaryMonths.concat(year.months)
        })
        // this.totalRow = summaryMonths.shift()
        this.mySummary = summaryMonths
      } else {
        this.totalRow = summary.shift()
        this.mySummary = summary
      }
      this.currentPage = 1

      this.createBarChartObject()
    },
    async loadSummary(filters) {
      this.loadedData = false
      try {
        const result = await getOrderSummary({
          group: 'byMonthAndType',
          filter: {
            type: ['bonus', 'stakeReward', 'drip'],
            // positionType: { $ne: 'cash' },
            ...filters,
          },
        })
        this.summary = result.data

        const isAllDate = !filters.date || Object.keys(filters.date).length === 0
        if (isAllDate) {
          this.$set(this, 'dateType', 'year')
        } else {
          this.$set(this, 'dateType', 'month')
        }

        this.changeType()
      } catch (error) {
        this.$root.$snackbar.checkAndOpenCommonLoadError(error)
      }
      this.loadedData = true
    },
  },
}
</script>

<style lang="scss">
#orders-rewards-by-month {
  .card-title {
    display: flex;
    align-items: center;
  }

  #orders-rewards-by-month-table {
    .items-flex {
      display: flex;
    }
  }
}
</style>
